// Stage 33: Bulletin Canvas — minimal Markdown → HTML renderer.
//
// This intentionally avoids `marked` + `dompurify` so we don't add new
// frontend dependencies. The iframe sandbox + CSP are the security
// boundary for everything in the panel body — including markdown, since
// the worker writer accepts an arbitrary body and the model is the
// author. The renderer here is a structural transform only:
//
//   - headings (#, ##, ###, ####)
//   - bold/italic/inline-code
//   - fenced code blocks with HTML escape
//   - unordered/ordered lists (single level)
//   - blockquotes
//   - links and images (URLs left as-is — the iframe CSP blocks any
//     external image and any outbound nav anyway)
//   - paragraphs separated by blank lines
//
// Anything not recognised drops through as escaped text. Embedded raw
// HTML is escaped, NOT injected — so a markdown body that includes a
// rogue `<script>` becomes visible text inside the iframe rather than
// executable script. (The iframe sandbox would block fetch/XHR even if
// it ran, but we still belt-and-brace at this layer.)

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inlineFormat(s: string): string {
  // 1. Inline code. Capture before bold/italic so backticks aren't
  //    further-processed.
  s = s.replace(
    /`([^`]+)`/g,
    (_m, code) => `<code>${escapeHtml(code)}</code>`,
  );
  // 2. Images: ![alt](src). Render before links.
  s = s.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    (_m, alt, src) =>
      `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}">`,
  );
  // 3. Links: [text](href).
  s = s.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_m, text, href) =>
      `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${escapeHtml(text)}</a>`,
  );
  // 4. Bold (**…**) and italic (*…*). Bold-first so it doesn't get
  //    eaten by the italic pass.
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  return s;
}

export function renderMarkdownToHtml(md: string): string {
  const lines = md.split(/\r?\n/);
  const out: string[] = [];

  let inFence = false;
  let fenceLang = '';
  let fenceBuffer: string[] = [];

  let listType: 'ul' | 'ol' | null = null;
  let inBlockquote = false;
  let paragraphBuffer: string[] = [];

  const flushParagraph = (): void => {
    if (paragraphBuffer.length === 0) return;
    const body = paragraphBuffer.map((s) => escapeHtml(s)).join(' ');
    out.push(`<p>${inlineFormat(body)}</p>`);
    paragraphBuffer = [];
  };
  const closeList = (): void => {
    if (listType !== null) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };
  const closeBlockquote = (): void => {
    if (inBlockquote) {
      out.push('</blockquote>');
      inBlockquote = false;
    }
  };
  const closeAllStructural = (): void => {
    flushParagraph();
    closeList();
    closeBlockquote();
  };

  for (const raw of lines) {
    if (inFence) {
      if (/^```/.test(raw.trim())) {
        const code = fenceBuffer.join('\n');
        const lang = fenceLang ? ` data-lang="${escapeHtml(fenceLang)}"` : '';
        out.push(`<pre${lang}><code>${escapeHtml(code)}</code></pre>`);
        inFence = false;
        fenceLang = '';
        fenceBuffer = [];
      } else {
        fenceBuffer.push(raw);
      }
      continue;
    }
    const fenceMatch = raw.match(/^```\s*([A-Za-z0-9_-]*)\s*$/);
    if (fenceMatch) {
      closeAllStructural();
      inFence = true;
      fenceLang = fenceMatch[1] ?? '';
      fenceBuffer = [];
      continue;
    }

    if (raw.trim().length === 0) {
      closeAllStructural();
      continue;
    }

    const headingMatch = raw.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      closeAllStructural();
      const level = headingMatch[1].length;
      const text = inlineFormat(escapeHtml(headingMatch[2]));
      out.push(`<h${level}>${text}</h${level}>`);
      continue;
    }

    const ulMatch = raw.match(/^[-*+]\s+(.*)$/);
    const olMatch = raw.match(/^\d+\.\s+(.*)$/);
    if (ulMatch || olMatch) {
      flushParagraph();
      closeBlockquote();
      const wantType: 'ul' | 'ol' = ulMatch ? 'ul' : 'ol';
      if (listType !== wantType) {
        closeList();
        out.push(`<${wantType}>`);
        listType = wantType;
      }
      const text = inlineFormat(escapeHtml((ulMatch ?? olMatch)![1]));
      out.push(`<li>${text}</li>`);
      continue;
    } else {
      closeList();
    }

    const quoteMatch = raw.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      if (!inBlockquote) {
        out.push('<blockquote>');
        inBlockquote = true;
      }
      const text = inlineFormat(escapeHtml(quoteMatch[1]));
      out.push(`<p>${text}</p>`);
      continue;
    } else {
      closeBlockquote();
    }

    paragraphBuffer.push(raw);
  }

  // Flush any open structures.
  if (inFence) {
    out.push(
      `<pre><code>${escapeHtml(fenceBuffer.join('\n'))}</code></pre>`,
    );
  }
  closeAllStructural();

  return out.join('\n');
}
